import angular from 'angular';
import '../core.module';
import { highlightSegments, normalizeSearchText } from '../helpers/string.helper';

export const COMMAND_RECENTS_LIMIT = 5;

const SCORE = {
  EXACT: 100,
  PREFIX: 80,
  WORD_PREFIX: 70,
  CONTAINS: 60,
  KEYWORD_EXACT: 45,
  KEYWORD_PREFIX: 40,
  KEYWORD_CONTAINS: 35,
  MODULE: 25,
  DESCRIPTION: 20,
  TOKENS: 10
};

export function commandId(state, params) {
  if (!params || !Object.keys(params).length) return state;
  const query = Object.keys(params)
    .sort()
    .map((key) => `${key}=${params[key]}`)
    .join('&');
  return `${state}?${query}`;
}

function textScore(text, term) {
  if (!text) return 0;
  if (text === term) return SCORE.EXACT;
  if (text.startsWith(term)) return SCORE.PREFIX;
  if (text.split(/[\s/&-]+/).some((word) => word.startsWith(term))) return SCORE.WORD_PREFIX;
  if (text.includes(term)) return SCORE.CONTAINS;
  return 0;
}

function keywordScore(keywords, term) {
  return keywords.reduce((best, keyword) => {
    let score = 0;
    if (keyword === term) score = SCORE.KEYWORD_EXACT;
    else if (keyword.startsWith(term)) score = SCORE.KEYWORD_PREFIX;
    else if (keyword.includes(term)) score = SCORE.KEYWORD_CONTAINS;
    return Math.max(best, score);
  }, 0);
}

export function scoreCommand(item, rawTerm) {
  const term = normalizeSearchText(rawTerm).trim();
  if (!term) return 0;
  const label = normalizeSearchText(item.label);
  const keywords = (item.keywords || []).map(normalizeSearchText);
  const module = normalizeSearchText(item.module);
  const description = normalizeSearchText(item.description);
  const direct = Math.max(
    textScore(label, term),
    keywordScore(keywords, term),
    module.includes(term) ? SCORE.MODULE : 0,
    description.includes(term) ? SCORE.DESCRIPTION : 0
  );
  if (direct) return direct;
  const haystack = [label, module, description, ...keywords].join(' ');
  const tokens = term.split(/\s+/).filter(Boolean);
  return tokens.length > 1 && tokens.every((token) => haystack.includes(token)) ? SCORE.TOKENS : 0;
}

export function decorateCommand(item, term) {
  return {
    ...item,
    labelSegments: highlightSegments(item.label, term),
    descriptionSegments: highlightSegments(item.description || '', term)
  };
}

class CommandCatalogService {
  constructor(
    MenuService,
    SessionService,
    StorageService,
    STORAGE_KEYS,
    ROUTE_PERMISSIONS,
    COMMAND_PALETTE_DETAILS,
    $state
  ) {
    this.MenuService = MenuService;
    this.SessionService = SessionService;
    this.StorageService = StorageService;
    this.STORAGE_KEYS = STORAGE_KEYS;
    this.ROUTE_PERMISSIONS = ROUTE_PERMISSIONS;
    this.details = COMMAND_PALETTE_DETAILS;
    this.$state = $state;
  }

  isAvailable(state) {
    return Boolean(this.$state.get(state));
  }

  canOpen(state, extraPermission) {
    return (
      this.isAvailable(state) &&
      this.SessionService.hasPermission(this.ROUTE_PERMISSIONS[state]) &&
      (!extraPermission || this.SessionService.hasPermission(extraPermission))
    );
  }

  build() {
    const pages = [];
    this.MenuService.getMenu().forEach((section) => {
      section.items
        .filter((item) => this.isAvailable(item.state))
        .forEach((item) => {
          const details = this.details[item.state] || {};
          pages.push({
            id: commandId(item.state),
            kind: 'page',
            group: 'pages',
            state: item.state,
            params: null,
            label: item.label,
            icon: item.icon,
            module: section.section,
            description: details.description || '',
            keywords: details.keywords || []
          });
        });
    });

    const shortcuts = [];
    Object.keys(this.details).forEach((state) => {
      const parent = pages.find(
        (page) => state === page.state || state.startsWith(`${page.state}.`)
      );
      if (!parent) return;
      (this.details[state].shortcuts || []).forEach((shortcut) => {
        if (!this.canOpen(state, shortcut.permission)) return;
        const params = shortcut.params || null;
        shortcuts.push({
          id: commandId(state, params),
          kind: 'page',
          group: 'shortcuts',
          state,
          params,
          label: shortcut.label,
          icon: shortcut.icon || parent.icon,
          module: parent.label,
          description: shortcut.description || '',
          keywords: shortcut.keywords || []
        });
      });
    });

    return pages.concat(shortcuts);
  }

  search(catalog, term) {
    return catalog
      .map((item, index) => ({ item, index, score: scoreCommand(item, term) }))
      .filter((entry) => entry.score > 0)
      .sort((a, b) => b.score - a.score || a.index - b.index)
      .map((entry) => decorateCommand(entry.item, term));
  }

  recentsKey() {
    const user = this.SessionService.user;
    return user && user.id ? `${this.STORAGE_KEYS.COMMAND_RECENTS}:${user.id}` : null;
  }

  readRecents() {
    const key = this.recentsKey();
    if (!key) return [];
    try {
      const stored = this.StorageService.get(key);
      return Array.isArray(stored) ? stored.filter((entry) => entry && entry.id) : [];
    } catch (error) {
      return [];
    }
  }

  recents(catalog) {
    return this.readRecents()
      .map((entry) => {
        if (entry.kind === 'page') {
          const item = catalog.find((candidate) => candidate.id === entry.id);
          return item ? decorateCommand({ ...item, group: 'recents' }, '') : null;
        }
        if (entry.kind === 'record' && this.canOpen(entry.state)) {
          return decorateCommand(
            {
              id: entry.id,
              kind: 'record',
              group: 'recents',
              state: entry.state,
              params: entry.params || null,
              label: String(entry.label || ''),
              icon: entry.icon || 'search',
              module: String(entry.module || ''),
              description: String(entry.description || ''),
              keywords: []
            },
            ''
          );
        }
        return null;
      })
      .filter(Boolean)
      .slice(0, COMMAND_RECENTS_LIMIT);
  }

  remember(item) {
    const key = this.recentsKey();
    if (!key || !item || !item.id) return;
    const entry =
      item.kind === 'record'
        ? {
            id: item.id,
            kind: 'record',
            state: item.state,
            params: item.params || null,
            label: item.label,
            icon: item.icon,
            module: item.module,
            description: item.description
          }
        : { id: item.id, kind: 'page' };
    const next = [entry]
      .concat(this.readRecents().filter((stored) => stored.id !== item.id))
      .slice(0, COMMAND_RECENTS_LIMIT);
    try {
      this.StorageService.set(key, next);
    } catch (error) {
      return;
    }
  }
}

CommandCatalogService.$inject = [
  'MenuService',
  'SessionService',
  'StorageService',
  'STORAGE_KEYS',
  'ROUTE_PERMISSIONS',
  'COMMAND_PALETTE_DETAILS',
  '$state'
];

angular.module('app.core').service('CommandCatalogService', CommandCatalogService);

export default CommandCatalogService;
